// Global
import React, { useId, useMemo } from 'react';

// Local
import styles from './RecipeBakeSection.module.scss';
import EnhancedRichText from 'components/helpers/Atoms/EnhancedRichText/EnhancedRichText';
import clsx from 'clsx';
import { decode } from 'html-entities';
import { PrepMethod, RecipeResponse } from 'lib/services/models/content-response';

// This component is specifically created for NAF recipes detail page
export type RecipeBakeSectionTableRow = {
  bake: string;
  temp: string;
  time: string;
};

export interface RecipeBakeSectionTableProps {
  className?: string;
  heading?: string;
  columnHeaders?: readonly [string, string, string];
  rows?: RecipeBakeSectionTableRow[];
  recipe?: RecipeResponse;
  prepMethods?: PrepMethod[] | null;
}

const defaultHeaders: [string, string, string] = ['Bake', 'Temp', 'Time'];

const escapeHtml = (value: string) => {
  return value
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#039;');
};

const stripHtmlToLines = (html: string) => {
  // Normalize <br> into line breaks, then remove remaining tags.
  const withNewlines = html.replace(/<br\s*\/?>/gi, '\n');
  const withoutParagraphs = withNewlines
    .replace(/<\/p>\s*<p[^>]*>/gi, '\n')
    .replace(/<p[^>]*>/gi, '\n')
    .replace(/<\/p>/gi, '\n');
  const withoutTags = withoutParagraphs.replace(/<[^>]*>/g, '');
  const decoded = decode(withoutTags);

  return decoded
    .split(/\n+/)
    .map((l) => l.trim())
    .filter(Boolean);
};

const parseBakeDescription = (descriptionHtml: string | null | undefined) => {
  const lines = stripHtmlToLines(descriptionHtml || '');

  const rows: RecipeBakeSectionTableRow[] = [];
  const notes: string[] = [];

  for (const line of lines) {
    if (!line.includes('|')) {
      notes.push(line);
      continue;
    }

    const parts = line
      .split('|')
      .map((p) => p.trim())
      .filter(Boolean);

    if (parts.length < 3) {
      notes.push(line);
      continue;
    }

    const bake = parts[0];
    const temp = parts[1];
    const time = parts.slice(2).join(' | ');

    rows.push({ bake, temp, time });
  }

  return { rows, notes };
};

const buildTableHtml = (
  caption: string,
  notesId: string | undefined,
  headers: [string, string, string],
  rows: RecipeBakeSectionTableRow[],
  notes?: string[]
) => {
  const headerHtml = headers.map((h) => `<th scope="col">${escapeHtml(h)}</th>`).join('');

  const bodyHtml = rows
    .map((r) => {
      return `
				<tr>
          <td><p>${escapeHtml(r.bake)}</p></td>
          <td><p>${escapeHtml(r.temp)}</p></td>
          <td><p>${escapeHtml(r.time)}</p></td>
				</tr>
			`;
    })
    .join('');

  const notesHtml = (notes || []).map((n) => `<p>${escapeHtml(n)}</p>`).join('');

  const captionHtml = `<caption class="screenReaderText">${escapeHtml(caption)}</caption>`;
  const hasNotes = !!(notes && notes.length > 0);
  const describedByAttr = notesId && hasNotes ? ` aria-describedby="${notesId}"` : '';
  const notesContainerHtml =
    notesId && hasNotes
      ? `<div id="${notesId}" class="naf-recipe-table__notes">${notesHtml}</div>`
      : '';

  // The additional wrapper classes are intentional:
  // - `.rte` comes from EnhancedRichText
  // - `.ck-content` + `figure.table` match existing global table styles in src/assets/_rte.scss
  return `
		<div class="ck-content">
      <figure class="table naf-recipe-table">
        <table class="naf-recipe-table__table"${describedByAttr}>
          ${captionHtml}
					<colgroup>
            <col />
            <col />
            <col />
					</colgroup>
					<thead>
						<tr>${headerHtml}</tr>
					</thead>
					<tbody>
						${bodyHtml}
					</tbody>
				</table>
        ${notesContainerHtml}
			</figure>
		</div>
	`;
};

export const RecipeBakeSection = (props: RecipeBakeSectionTableProps) => {
  const {
    className,
    heading = 'Bake Temp and Time',
    columnHeaders = defaultHeaders,
    rows,
    recipe,
    prepMethods,
  } = props;

  const sourcePrepMethods = prepMethods ?? recipe?.PrepMethods ?? [];
  //   const sourcePrepMethods = hardcodedPrepMethods;

  const reactId = useId();
  const notesId = useMemo(() => {
    const safe = reactId.replace(/[^a-zA-Z0-9_-]/g, '');
    return safe ? `naf-recipe-table-notes-${safe}` : undefined;
  }, [reactId]);

  const bakeMethod = sourcePrepMethods?.find(
    (m) => (m?.Header || '').trim().toLowerCase() === 'bake'
  );

  const parsed = useMemo(() => {
    if (!bakeMethod?.Description) return null;
    const { rows: parsedRows, notes } = parseBakeDescription(bakeMethod.Description);
    if (parsedRows.length === 0) return null;
    return { rows: parsedRows, notes };
  }, [bakeMethod?.Description]);

  const shouldRender = !!parsed || !!(rows && rows.length > 0);

  // If there are no parsed bake rows and no explicit rows, hide.
  const html = useMemo(() => {
    if (!shouldRender) return '';
    const effectiveRows = parsed?.rows ?? rows ?? [];
    const effectiveNotes = parsed?.notes;
    return buildTableHtml(
      heading,
      notesId,
      columnHeaders as [string, string, string],
      effectiveRows,
      effectiveNotes
    );
  }, [columnHeaders, heading, notesId, parsed, rows, shouldRender]);

  if (!shouldRender) {
    return null;
  }

  return (
    <section className={clsx(styles.wrapper, className)} aria-label={heading}>
      <div className={styles.tableWrapper}>
        <EnhancedRichText className={styles.richText} value={html} />
      </div>
    </section>
  );
};

export default RecipeBakeSection;
